import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  LockClosedIcon,
  ShieldCheckIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';

const ProfileSettings: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: ''
  });
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [savedAddresses, setSavedAddresses] = useState<any[]>([]);
  const [showAddAddress, setShowAddAddress] = useState(false);

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Implement profile update API call
    console.log('Updating profile:', profileData);
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwords.new !== passwords.confirm) {
      alert('New passwords do not match');
      return;
    }
    // TODO: Implement password change API call
    console.log('Changing password');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Profile & Settings</h1>
          <p className="mt-2 text-gray-600">Manage your account settings and preferences</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Tabs Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-volexx-lg shadow-volexx-md overflow-hidden">
              <button
                onClick={() => setActiveTab('profile')}
                className={`w-full px-4 py-3 text-left flex items-center space-x-3 transition-colors ${
                  activeTab === 'profile' ? 'bg-volexx-blue-50 text-volexx-blue-600 border-l-4 border-volexx-blue-600' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <UserIcon className="h-5 w-5" />
                <span className="font-semibold">Edit Profile</span>
              </button>
              <button
                onClick={() => setActiveTab('addresses')}
                className={`w-full px-4 py-3 text-left flex items-center space-x-3 transition-colors ${
                  activeTab === 'addresses' ? 'bg-volexx-blue-50 text-volexx-blue-600 border-l-4 border-volexx-blue-600' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <MapPinIcon className="h-5 w-5" />
                <span className="font-semibold">Saved Addresses</span>
              </button>
              <button
                onClick={() => setActiveTab('security')}
                className={`w-full px-4 py-3 text-left flex items-center space-x-3 transition-colors ${
                  activeTab === 'security' ? 'bg-volexx-blue-50 text-volexx-blue-600 border-l-4 border-volexx-blue-600' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <LockClosedIcon className="h-5 w-5" />
                <span className="font-semibold">Security</span>
              </button>
              <button
                onClick={() => setActiveTab('preferences')}
                className={`w-full px-4 py-3 text-left flex items-center space-x-3 transition-colors ${
                  activeTab === 'preferences' ? 'bg-volexx-blue-50 text-volexx-blue-600 border-l-4 border-volexx-blue-600' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <GlobeAltIcon className="h-5 w-5" />
                <span className="font-semibold">Preferences</span>
              </button>
            </div>
          </div>

          {/* Content Area */}
          <div className="lg:col-span-3">
            {/* Edit Profile */}
            {activeTab === 'profile' && (
              <div className="bg-white rounded-volexx-lg shadow-volexx-md p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Edit Profile</h2>
                <form onSubmit={handleProfileUpdate} className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name
                    </label>
                    <div className="relative">
                      <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        value={profileData.name}
                        onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="email"
                        value={profileData.email}
                        onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <div className="relative">
                      <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="tel"
                        value={profileData.phone}
                        onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Address
                    </label>
                    <div className="relative">
                      <MapPinIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <textarea
                        value={profileData.address}
                        onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
                        rows={3}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500"
                      />
                    </div>
                  </div>

                  <button type="submit" className="btn-primary w-full">
                    Save Changes
                  </button>
                </form>
              </div>
            )}

            {/* Saved Addresses */}
            {activeTab === 'addresses' && (
              <div className="bg-white rounded-volexx-lg shadow-volexx-md p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Saved Addresses</h2>
                  <button
                    onClick={() => setShowAddAddress(true)}
                    className="btn-primary"
                  >
                    Add Address
                  </button>
                </div>

                {savedAddresses.length === 0 ? (
                  <div className="text-center py-12">
                    <MapPinIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No saved addresses yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {savedAddresses.map((address, index) => (
                      <div key={index} className="border border-gray-200 rounded-volexx p-4">
                        <p className="font-semibold text-gray-900">{address.label}</p>
                        <p className="text-sm text-gray-600 mt-1">{address.address}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Security Settings */}
            {activeTab === 'security' && (
              <div className="bg-white rounded-volexx-lg shadow-volexx-md p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Security Settings</h2>
                
                <form onSubmit={handlePasswordChange} className="space-y-6 mb-8">
                  <h3 className="text-lg font-semibold text-gray-900">Change Password</h3>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Current Password
                    </label>
                    <input
                      type="password"
                      value={passwords.current}
                      onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      New Password
                    </label>
                    <input
                      type="password"
                      value={passwords.new}
                      onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      value={passwords.confirm}
                      onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500"
                    />
                  </div>

                  <button type="submit" className="btn-primary w-full">
                    Update Password
                  </button>
                </form>

                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Two-Factor Authentication</h3>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-volexx">
                    <div className="flex items-center">
                      <ShieldCheckIcon className="h-6 w-6 text-gray-400 mr-3" />
                      <div>
                        <p className="font-semibold text-gray-900">2FA Status</p>
                        <p className="text-sm text-gray-500">Currently disabled</p>
                      </div>
                    </div>
                    <button className="px-4 py-2 bg-volexx-blue-500 text-white rounded-volexx font-semibold hover:bg-volexx-blue-600">
                      Enable 2FA
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Preferences */}
            {activeTab === 'preferences' && (
              <div className="bg-white rounded-volexx-lg shadow-volexx-md p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Preferences</h2>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Language
                    </label>
                    <select className="w-full px-4 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500">
                      <option>English</option>
                      <option>Spanish</option>
                      <option>French</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Currency
                    </label>
                    <select className="w-full px-4 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500">
                      <option>USD ($)</option>
                      <option>EUR (€)</option>
                      <option>GBP (£)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Preferred Vehicle Type
                    </label>
                    <select className="w-full px-4 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500">
                      <option>Bicycle</option>
                      <option>Motorbike</option>
                      <option>Sedan</option>
                      <option>SUV</option>
                      <option>Van</option>
                    </select>
                  </div>

                  <button className="btn-primary w-full">
                    Save Preferences
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;
